import { acceptHMRUpdate, defineStore } from 'pinia'
import type { Allocation, OptionView, VoteTotals, Window } from '@/client/engine/api.ts'
import type { EngineChip } from '@/client/engine/labels.ts'
import type { EngineLogEntry } from '@/client/engine/log.ts'

export type EngineDecision = {
    decision: number
    seat: number // The user's seat
    window: Window
    options: OptionView[]
    // What the choice is for, when the options alone do not say ( a referendum's terms )
    prompt?: string | null
    // The previous step of this multi-step choice can be taken back
    undo: boolean
    // An allocation of points under way : answered whole in the panel
    allocation?: Allocation | null
    // The votes the user can cast all at once, each way
    votes?: VoteTotals | null
    // The combat under way, if any
    combat: EngineCombat | null
    // Its pass declines to block
    decline: boolean
}

export type EngineCombat = {
    minions: [string, string] // The acting minion's side first
    // One of them is the user's : else the user is asked as a bystander ( Rötschreck, Abjure )
    involved: boolean
    round: number
    range: string
    // Each side, the acting minion's first ( when the engine tells them )
    sides: EngineCombatSideView[]
}

// A side of the combat : its minion, whether it is the user's, its strike once declared ( its name and damage ),
// its maneuvers and presses for the round
export type EngineCombatSideView = {
    name: string
    own: boolean
    strike?: string
    maneuvers: number
    presses: number
}

/**
 * State of a game played with the rules engine ( GameType.Engine ) :
 * the decision the user must take, and the end of the game.
 */
const NOTICE_DURATION = 4000
let noticeTimeout: ReturnType<typeof setTimeout> | undefined

export const useEngineStore = defineStore('engine', {
    state: () => ({
        // The decision the user must take now, if any
        pending: null as EngineDecision | null,
        // Options ( by index ) the user's last move can mean : put forward in the panel
        focus: [] as number[],
        // What the engine is waiting for, when it's not the user
        status: '',
        // The connection to the engine server dropped : the game being joined again
        rejoining: false,
        // A choice made on the table, under way : the options ( by index ) still possible
        staging: null as number[] | null,
        // Victory points per seat, once the game is over
        result: null as number[] | null,
        // The referendum under way : its card and the terms chosen, as lines
        terms: [] as string[],
        // The referendum card's text : what passing does
        referendumText: '',
        // The cards played during the action under way, by whom ( the log's line ), until it is over
        played: [] as { decision: number; player: string; text: string; option: OptionView }[],
        // The last hand ( or library top ) the user looked at : its seat, whether the library is the
        // user's own, and its cards, until closed
        looked: null as { seat: number; library: boolean; own: boolean; cards: number[] } | null,
        // The cards on their way to play ( limbo ), by oid : not in play yet
        limbo: [] as string[],
        // A minion's counted markers ( hostage, nightmare, torture, a lasting effect's counters ),
        // by oid : round chips on it, the count on them ( see minionMarkers )
        chips: {} as Record<string, EngineChip[]>,
        // The cards on a card in play, by its oid : their oids ( see engine/stacks.ts )
        stacks: {} as Record<string, string[]>,
        // During a bleed, its amount ( the engine's, a card's bleed too )
        bleed: null as number | null,
        // During an influence phase, the active seat's transfers left
        transfers: null as number | null,
        // A block's combat is under way ( whoever decides now )
        inCombat: false,
        // A withdrawal from the game, by player oid : announced, or done ( out of the game with
        // half a victory point, not ousted though the engine counts the seat out )
        withdrawals: {} as Record<string, 'withdrawing' | 'withdrew'>,
        // Why the user's last move on the table was no decision, for a moment ( beside the Pass )
        notice: '',
        // The game's log ( see log.ts ) : its lines, the seats' colors
        log: [] as EngineLogEntry[],
        // Its lines added or grown, counted : the log follows them
        logVersion: 0,
        // The lines the user folded or unfolded, by key ( the others : open while under way )
        logOpen: {} as Record<string, boolean>,
        seatColors: [] as string[],
    }),
    actions: {
        // Shown a few seconds, a later one replacing it
        tellNotice(message: string) {
            this.notice = message
            clearTimeout(noticeTimeout)
            noticeTimeout = setTimeout(() => (this.notice = ''), NOTICE_DURATION)
        },
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useEngineStore, import.meta.hot))
}
